import type {
  IDemo,
  IVariable,
  Opportunity,
  PlayAsset,
} from '@carboncopyco/types';
import { SideTabs } from '@carboncopyco/ui';
import { Tab } from '@headlessui/react';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { iconByAssetType } from '../../../InfoPage/PlaybookPage/utils';
import { VerticalTabsModal } from '../../../VerticalTabsModal';
import { useGiveADemo } from '../GiveADemoContext';
import { getEntityTitle } from '../utils';
import { AssetTabSwitch } from './AssetTabSwitch';

type TabValue = string;

export type CustomizeModalFormState = {
  forClient: string;
  opportunity?: Opportunity | null;
  variables: Record<IDemo['id'], IVariable[]>;
  speakerNotes: Record<IDemo['id'], string>;
};

type CustomizeModalProps = {
  open: boolean;
  assets: PlayAsset['assets'];
  onSubmit: (values: CustomizeModalFormState) => void;
  onClose: () => void;
};

export const CustomizeModal = ({
  open,
  assets,
  onSubmit,
  onClose,
}: CustomizeModalProps) => {
  const { submissionValues, setSubmissionValues } = useGiveADemo();
  const sideTabs = assets.map((asset) => ({
    value: String(asset.entity.id),
    label: getEntityTitle(asset.entity),
    icon: iconByAssetType[asset.assetType],
  }));

  const initialValues = useMemo(
    () =>
      assets.reduce(
        (acc, asset) => ({
          ...acc,
          variables: {
            ...acc.variables,
            [asset.entity.id]: [
              ...submissionValues.variables[asset.entity.id],
            ].reverse(),
          },
          speakerNotes: {
            ...acc.speakerNotes,
            [asset.entity.id]:
              submissionValues.speakerNotes[asset.entity.id] ?? '',
          },
        }),
        {} as CustomizeModalFormState
      ),
    [assets, submissionValues]
  );

  return (
    <Formik<CustomizeModalFormState>
      enableReinitialize
      initialValues={initialValues}
      onSubmit={(values) => {
        const valuesToSubmit = {
          ...submissionValues,
          variables: values.variables,
          speakerNotes: values.speakerNotes,
        };
        setSubmissionValues(valuesToSubmit);
        onSubmit(valuesToSubmit);
      }}
    >
      {({ handleSubmit: submit, dirty }) => {
        return (
          <VerticalTabsModal
            title="Customize your demo"
            open={open}
            onSave={submit}
            isSaveDisabled={!dirty}
            confirmOnCancel={dirty}
            onClose={onClose}
          >
            <SideTabs<TabValue> items={sideTabs}>
              {sideTabs.map((tab, index) => {
                const selectedAssetType = assets[index].assetType;
                return (
                  <Tab.Panel
                    key={tab.value}
                    className="h-full w-full flex flex-col flex-1 overflow-y-auto"
                  >
                    <AssetTabSwitch
                      selectedId={tab.value}
                      entityType={selectedAssetType}
                    />
                  </Tab.Panel>
                );
              })}
            </SideTabs>
          </VerticalTabsModal>
        );
      }}
    </Formik>
  );
};
